import React, { useRef } from 'react';

interface PhotoUploadProps {
  onPhotoSelect: (file: File) => void;
  preview?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ onPhotoSelect, preview }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (match Profile.tsx validation)
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, GIF, BMP, WEBP, SVG).');
        return;
      }

      // Validate file size (10MB max like Profile.tsx)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit.');
        return;
      }

      onPhotoSelect(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex items-center space-x-4">
      <div className="w-24 h-24 overflow-hidden bg-gray-200 border-2 border-gray-300 border-dashed rounded-lg">
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className="object-cover w-full h-full"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={handleClick}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {preview ? 'Change Photo' : 'Upload Photo'}
        </button>
        <p className="mt-1 text-xs text-gray-500">
          JPG, PNG, GIF, BMP, WEBP, SVG - max 10MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/bmp,image/webp,image/svg+xml"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default PhotoUpload;